import { Button } from '@utilitywarehouse/hearth-react';
import { useNavigate } from 'react-router-dom';

import { Dialog, DialogActions, DialogID, useDialog } from '@components/Dialog';
import { useEnergyContext } from '@features/Energy/Provider';
import useClearEnergyRemoteFormState from '@features/EnergyDetailsForm/EnergyForm/hooks/useClearEnergyRemoteForm';

interface SaveChangesModalProps {
  handleGetQuote(event: React.MouseEvent<HTMLButtonElement>): void;
}

interface SaveChangesModalUIProps {
  dialogId: DialogID;
  onDontSave: () => void;
  onSave: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

const SaveChangesModalUI = ({
  dialogId,
  onDontSave,
  onSave,
}: SaveChangesModalUIProps) => (
  <Dialog
    id={dialogId}
    data-testid="save-changes-dialog"
    heading="Save changes?"
    description="Would you like to save the changes you've made to your energy details?"
    preventDismiss
    hideCloseButton
  >
    <DialogActions>
      <Button
        type="button"
        variant="outline"
        colorScheme="destructive"
        data-testid="save-changes-dont-save-button"
        onClick={onDontSave}
      >
        Don't save
      </Button>
      <Button
        type="button"
        variant="solid"
        data-testid="save-changes-save-button"
        onClick={onSave}
      >
        Save
      </Button>
    </DialogActions>
  </Dialog>
);

const SaveChangesModal = ({ handleGetQuote }: SaveChangesModalProps) => {
  const navigate = useNavigate();
  const dialog = useDialog(DialogID.SAVE_CHANGES_ENERGY);
  const clearEnergyRemoteFormState = useClearEnergyRemoteFormState();
  const { journeyPaths } = useEnergyContext();

  const handleDontSave = () => {
    clearEnergyRemoteFormState();
    dialog.setOpen(false);
    navigate(journeyPaths.tariff);
  };

  const handleSave = (event: React.MouseEvent<HTMLButtonElement>) => {
    dialog.setOpen(false);
    handleGetQuote(event);
  };

  return (
    <SaveChangesModalUI
      dialogId={dialog.id}
      onDontSave={handleDontSave}
      onSave={handleSave}
    />
  );
};

export type { SaveChangesModalProps, SaveChangesModalUIProps };

export { SaveChangesModal, SaveChangesModalUI };
